/**
 * Column policy and localStorage cache-key resolution for the Buildings listing.
 *
 * Rules from the contract:
 *  - allowed: every key that may appear in visibleColumnKeys (= ALL_COLUMNS from constants.js).
 *  - defaults: keys visible in the default visible set (= DEFAULT_COLUMNS from constants.js, i.e. all except externalId).
 *  - required: keys that are always visible and cannot be hidden.
 *  - Non-sortable keys: siteId, inspectionsCount (IP-15590).
 *  - Cache keys stay compatible with the legacy Kendo shapes.
 *  - In account scope the `account` column is dropped from defaults (redundant when scoped).
 */
// ---------------------------------------------------------------------------
// All known column keys (display order)
// ---------------------------------------------------------------------------
/** All user-controllable column keys in display order (matches ALL_COLUMNS in constants.js). */
export const ALL_BUILDING_COLUMNS = [
    'name',
    'address',
    'city',
    'state',
    'zip',
    'account',
    'territory',
    'contractExpiration',
    'buildingNumber',
    'referenceNumber',
    'siteId',
    'externalId',
    'inspectionsCount'
];
/** The `name` column is always required (primary link cell). */
const REQUIRED_COLUMNS = ['name'];
/**
 * Opt-in columns: allowed in every scope, but never in the default visible set.
 * `externalId` is opt-in because the values are opaque integration identifiers
 * (UUIDs in practice) that most users don't need to see by default.
 */
const OPT_IN_COLUMNS = ['externalId'];
// ---------------------------------------------------------------------------
// Column policy factory
// ---------------------------------------------------------------------------
/**
 * Returns the column policy for a given scope.
 *
 * - standalone: all columns visible by default except opt-in ones.
 * - account: `account` column removed from defaults (redundant when already scoped).
 */
export function columnPolicyForScope(scope) {
    const allowed = [...ALL_BUILDING_COLUMNS];
    let defaults;
    if (scope.type === 'account') {
        defaults = allowed.filter((c) => c !== 'account');
    }
    else {
        defaults = [...allowed];
    }
    // Opt-in columns stay out of every default set, regardless of scope.
    defaults = defaults.filter((c) => !OPT_IN_COLUMNS.includes(c));
    return {
        allowed,
        defaults,
        required: [...REQUIRED_COLUMNS]
    };
}
// ---------------------------------------------------------------------------
// Cache key factory
// ---------------------------------------------------------------------------
/**
 * Returns the localStorage cache key for a given scope.
 * Matches the legacy Kendo keys used by Rails views.
 *
 * standalone:  kendo/buildings/index
 * account:     kendo/buildings/index/account-${accountId}
 */
export function cacheKeyForScope(scope) {
    switch (scope.type) {
        case 'account':
            return `kendo/buildings/index/account-${scope.id}`;
        case 'standalone':
        default:
            return 'kendo/buildings/index';
    }
}
